import * as echarts from 'echarts/core';
import { cloneDeep, intersection, uniq } from 'lodash';

import {
    globalLocationId,
    globalMapCountryLocationsIds,
    locationsWithDetailedMapView,
} from '../../config';
import nonGlobalMapTypeOverrides from './country-specific-map-type-overrides.json';
import { BOROUGH_LEVEL, NATIONAL_LEVEL, PROVINCE_LEVEL, REGION_LEVEL } from './map-detail-levels';
import fullMap from './map.json';

const DETAIL_LEVEL_TO_IHME_LEVEL_MAP = {
    [NATIONAL_LEVEL]: 3,
    [PROVINCE_LEVEL]: 4,
    [REGION_LEVEL]: 5,
    [BOROUGH_LEVEL]: 6,
};

const LOCATION_TOP_DETAIL_LEVELS = {
    ['163']: DETAIL_LEVEL_TO_IHME_LEVEL_MAP[PROVINCE_LEVEL], //'India'
};

const getMapOfLocations = (locations: number[], mapType: number): object => {
    const stringLocationIds = locations.map((id) => id.toString());
    const map = cloneDeep(fullMap);
    if (mapType !== globalLocationId) {
        const overridesMap = cloneDeep(nonGlobalMapTypeOverrides);
        const idsToOverride = overridesMap.features.map((feature) => feature.properties.name);
        const fullMapFeatures = map.features.filter(
            (feature) => !idsToOverride.includes(feature.properties.name)
        );
        map.features = [...fullMapFeatures, ...overridesMap.features];
    }
    map.features = map.features.filter((feature) => {
        const id = feature.properties.name;
        return stringLocationIds.includes(id);
    });
    return map;
};

export const updateEchartsMapVisibleLocations = (
    locations: number[],
    mapType: number
): [string, number] => {
    const map = getMapOfLocations(locations, mapType);
    const itemsAmount = map.features.length;
    const mapKey = map.features
        .map((feature) => Number(feature.properties.name).toString(36))
        .join('');
    if (!echarts.getMap(mapKey)) {
        echarts.registerMap(mapKey, map);
    }
    return [mapKey, itemsAmount];
};

const getHierarchyObject = (locationId: number, hierarchy): object =>
    hierarchy.find((location) => location.id === locationId);

const addSubnationalsToList = (locationId, list, detailLevel, hierarchy, refinedGranularity) => {
    if (!(locationId && hierarchy && refinedGranularity)) {
        return [];
    }

    const locationInfo = hierarchy.find(({ id }) => id === locationId);
    const locationLevel = locationInfo.level;

    const allKids = hierarchy.filter((location) => {
        const parentCountryId = location[`level${locationLevel}_parent_id`];
        const { level } = location;

        if (parentCountryId !== locationId) {
            return false;
        }

        const topDetailLevel = LOCATION_TOP_DETAIL_LEVELS[parentCountryId]
            ? Math.min(LOCATION_TOP_DETAIL_LEVELS[parentCountryId], detailLevel)
            : detailLevel;

        return parentCountryId === locationId && level >= 4 && level <= topDetailLevel;
    });

    const allKidsIds = allKids.map((kid) => kid.id);

    const partOfGranularityKids = intersection(refinedGranularity, allKidsIds);

    let refinedKids = [];

    if (partOfGranularityKids.length) {
        refinedKids = uniq(partOfGranularityKids.map((id) => getLeafs(id, hierarchy)).flat());
    }

    const parentsToFilter = [];
    refinedKids.forEach(
        ({ level3_parent_id, level4_parent_id, level5_parent_id, level6_parent_id }) => {
            parentsToFilter.push(
                level3_parent_id,
                level4_parent_id,
                level5_parent_id,
                level6_parent_id
            );
        }
    );

    list.push(...refinedKids.map((kid) => kid.id));

    const idsToFilter = uniq(parentsToFilter);
    if (parentsToFilter && parentsToFilter.length) {
        list = list.filter((id) => !idsToFilter.includes(id));
    }
    return list;
};

const getLeafs = (locationId, hierarchy): number[] => {
    const location = getHierarchyObject(locationId, hierarchy);
    const parentId = location[`level${location.level - 1}_parent_id`];
    const leafs = hierarchy.filter(
        (location) => location[`level${location.level - 1}_parent_id`] === parentId
    );
    return leafs;
};

export const getLocationIdByName = ({ name, hierarchy }) =>
    hierarchy.find((location) => location.name === name.replace('The', '').trim())?.id;

export const getLocationsForLevel = (detailLevel, mapType, refinedGranularity, hierarchy) => {
    const isGlobal = mapType === globalLocationId;

    let locationIds = isGlobal ? globalMapCountryLocationsIds.split(',').map(Number) : [mapType];
    const availableLocationsWithSubnationals = isGlobal ? locationsWithDetailedMapView : [mapType];

    availableLocationsWithSubnationals.forEach((locationId) => {
        locationIds = addSubnationalsToList(
            locationId,
            locationIds,
            DETAIL_LEVEL_TO_IHME_LEVEL_MAP[detailLevel],
            hierarchy,
            refinedGranularity
        );
    });

    return locationIds;
};

// TODO: it return a string but should return a number! please fix
export const getMaxLocationDetailLevel = (locationId, refinedGranularity, hierarchy): number => {
    if (!hierarchy) {
        return BOROUGH_LEVEL;
    }
    const ihmeLevelToDetailLevelMap = {};
    Object.entries(DETAIL_LEVEL_TO_IHME_LEVEL_MAP).forEach(([detailLevel, ihmeLevel]) => {
        ihmeLevelToDetailLevelMap[ihmeLevel] = detailLevel;
    });

    const locationIds = getLocationsForLevel(
        BOROUGH_LEVEL,
        locationId,
        refinedGranularity,
        hierarchy
    );

    const maxIhmeLevel = Math.max(
        ...locationIds.map((id) => hierarchy.find((location) => location.id === id).level)
    );

    return ihmeLevelToDetailLevelMap[maxIhmeLevel];
};
